<script>
import Form from '@classes/Form'
import store from '@state/store'
import { allergyService } from '@services/AllergyService'
import { healthRecordService } from '@services/HealthRecordService'
import { medicalDateFormatter, isBeforeDate } from '@utils/medical-date'
import { authComputed, authMethods, optionsComputed } from '@state/helpers'
import ComboBox from '@components/combo-box.vue'
import MedicalDatePickerDialog from '@components/dialogs/medical-date-picker-dialog.vue'
import hrMixin from '@src/mixins/hr-mixin.vue'
import ColoredRangeSlider from '@/src/components/colored-range-slider.vue'
export default {
  page: {
    title: 'Allergy Editor',
    meta: [{ name: 'description', content: 'The Allergy Editor page.' }],
  },
  components: {
    MedicalDatePickerDialog,
    ComboBox,
    ColoredRangeSlider,
  },
  mixins: [hrMixin],
  props: {
    formDialog: {
      type: Boolean,
      default: false,
    },
    formChanged: {
      type: Boolean,
      default: false,
    },
    formSaved: {
      type: Boolean,
      default: false,
    },
    formSavedAnother: {
      type: Boolean,
      default: false,
    },
    attachmentDeleted: {
      type: Boolean,
      default: false,
    },
    create: {
      type: Boolean,
      default: false,
    },
    edit: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: null,
    },
    id: {
      type: [Number, String],
      default: 0,
    },
    resource: {
      type: String,
      default: 'allergy',
    },

    show: {
      type: Boolean,
      default: false,
    },
    unauth: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      allergyForm: new Form({
        allergy: '',
        start: null,
        start_date_part: '',
        last_occurrence: null,
        last_occurrence_date_part: '',
        category: '',
        type: 'allergy',
        criticality: 1,
        reaction: '',
        verification_status: '',
        note: '',
        private: false,
      }),
      medicalDateStartPickerDialog: false,
      medicalDateEndPickerDialog: false,
      allergyError: null,
      attachments: [],
      originalAttachments: [],
      allergyIcons: {
        environment: 'mdi-leaf',
        food: 'mdi-pizza',
        medication: 'mdi-pill',
        other: 'mdi-dots-horizontal',
      },
      showHRActions: true,
    }
  },
  computed: {
    ...authComputed,
    ...optionsComputed,
    allergyTypeExample() {
      switch (this.allergyForm.category) {
        case '':
          return 'Allergy'
        case 'environment':
          return 'e.g. pollen'
        case 'food':
          return 'e.g. Peanut'
        case 'medication':
          return 'e.g. Amoxil'
        default:
          return 'e.g. Latex'
      }
    },
    categories() {
      return this.getAllergyOptions?.category
        ? this.getAllergyOptions.category
        : []
    },
    categoriesFromIcons: {
      get() {
        for (let i = 0; i < this.categories.length; i++) {
          if (this.categories[i].key === this.allergyForm.category) {
            return this.categories[i]
          }
        }
        return ''
      },
      set(value) {
        if (value) {
          this.allergyForm.category = value.key
        }
      },
    },
    types() {
      return this.getAllergyOptions?.type ? this.getAllergyOptions.type : []
    },
    criticalities() {
      return this.getAllergyOptions?.criticality
        ? this.getAllergyOptions.criticality
        : []
    },
    criticalitiesLabels() {
      const result = []
      const criticalities = this.criticalities

      if (criticalities.length > 0) {
        for (let i = 0; i < criticalities.length; i++) {
          result.push(criticalities[i].value)
        }
      }

      return result
    },
    reactions() {
      return this.getAllergyOptions?.reaction
        ? this.getAllergyOptions.reaction
        : []
    },
    verificationStatuses() {
      return this.getAllergyOptions?.verification_status
        ? this.getAllergyOptions.verification_status
        : []
    },
    canSaveAllergy() {
      if (
        (this.allergyForm.start ||
          this.allergyForm.start_date_part === 'past') &&
        this.allergyForm.allergy &&
        this.allergyForm.category &&
        this.allergyForm.type &&
        this.allergyForm.criticality &&
        this.allergyForm.verification_status &&
        this.allergyError === null &&
        this.dateError === null
      ) {
        return true
      }
      return false
    },
  },
  watch: {
    allergyForm: {
      handler(newVal, oldVal) {
        if (newVal) {
          this.$emit('update:formChanged', true)
        }
      },
      deep: true,
    },
  },
  async mounted() {
    try {
      if (!this.getAllergyOptions) {
        const allergyOptions = (await allergyService.options()).data
        if (allergyOptions) {
          store.dispatch('options/setAllergyOptions', allergyOptions)
        }
      }
      if (this.edit) {
        if (this.item.patient_id !== null) {
          this.showHRActions = false
        }
        this.itemToForm(this.resource, this.item)
      }
    } catch (error) {
      console.warn(error)
    }
  },
  methods: {
    ...authMethods,
    setMedicalDate(medicalDate, past) {
      const field = this.medicalDatePickerName
      const fieldPart = this.medicalDatePickerName + '_date_part'

      const medicalDateObject = medicalDateFormatter(medicalDate)

      this.allergyForm[field] = medicalDateObject.date
      this.allergyForm[fieldPart] = this.determinePast(past, medicalDateObject)
      this.medicalDateStartPickerDialog = false
      this.medicalDateEndPickerDialog = false
    },
    clearForm() {
      this.allergyForm.reset()
      this.message = false
      this.attachments = []
      this.isAnother = false
      this.isContinue = false
      this.allergyError = null
      this.dateError = null
    },
    async getAllergyResponse() {
      if (this.edit) {
        return (
          await healthRecordService.update(
            this.item.hr_id,
            this.allergyForm.removeEmptyStringFromData()
          )
        ).data
      } else {
        return (
          await allergyService.create(this.allergyForm.removeEmptyFromData())
        ).data
      }
    },
    async deleteAttachment(id) {
      try {
        const response = await allergyService.attachmentDelete(id)
        if (response) {
          this.originalAttachments = this.originalAttachments.filter(function (
            obj
          ) {
            return obj.id !== id
          })
          this.$emit('update:attachmentDeleted', true)
        }
      } catch (error) {
        console.warn(error)
      }
    },
    setErrors(error) {
      this.allergyForm.onFail(error.response.data)
      if (this.hasResponse) {
        this.message = true
        if (this.formDialog) {
          this.$emit('update:formSaved', true)
        }
      }
      this.allergyError = error
    },
    async tryToSave(action) {
      this.resetFlagsAndErrors('allergyError', action)
      try {
        if (
          this.allergyForm.start &&
          isBeforeDate(
            this.allergyForm.start,
            new Date().toISOString().split('T')[0]
          )
        ) {
          this.dateError = this.$t('start-after-today')
          this.scrollToErrors()
          return
        }
        if (
          this.allergyForm.start &&
          this.allergyForm.last_occurrence &&
          isBeforeDate(this.allergyForm.start, this.allergyForm.last_occurrence)
        ) {
          this.dateError = this.$t('end-before-start')
          this.scrollToErrors()
          return
        }
        const response = await this.getAllergyResponse()

        if (response) {
          this.hasResponse = true
          let responseAttachments

          if (this.attachments.length > 0) {
            responseAttachments = (
              await allergyService.attachments(
                this.attachments,
                response.hr_id,
                'allergy'
              )
            ).data
            console.warn(responseAttachments)
          }

          await this.fetchWizardData()

          if (!this.edit) {
            this.$emit('change-index', this.resource)
          }

          this.message = true
          if (
            action === 'continue' &&
            store.state.calender.addNewRecord === 'true'
          ) {
            this.$emit('update:formSaved', true)
            this.$emit('close-dialog')
            if (document.getElementById('getAllData'))
              document.getElementById('getAllData').click()
          }

          if (action === 'continue') {
            this.$emit('update:formSaved', true)
            this.edit ? this.$emit('close-dialog') : this.$emit('back')
            if (document.getElementById('getAllData'))
              document.getElementById('getAllData').click()
          }
          if (action === 'another') {
            this.$emit('update:formSavedAnother', true)
            this.clearForm()
            this.$emit('add-another')
            if (document.getElementById('updateGetAllData'))
              document.getElementById('updateGetAllData').click()
          }
        }
      } catch (error) {
        console.warn(error)
        this.setErrors(error)
        this.isAnother = false
        this.isContinue = false
        this.scrollToErrors()
      } finally {
        this.tryingToSave = false
      }
    },
    itemToForm(res, item) {
      if (item.record.start) {
        this.allergyForm.start = item.record.start
      }
      this.allergyForm.start_date_part = item.record.start_date_part
      if (item.record.last_occurrence) {
        this.allergyForm.last_occurrence = item.record.last_occurrence
      }
      this.allergyForm.last_occurrence_date_part =
        item.record.last_occurrence_date_part
      this.allergyForm.allergy = item.record.allergy
      this.allergyForm.category = item.record.category?.key
      this.allergyForm.type = item.record.type
      this.allergyForm.criticality = item.record.criticality?.key
      this.allergyForm.reaction = item.record.reaction?.key
      this.allergyForm.verification_status =
        item.record.verification_status?.key
      this.allergyForm.note = item.note
      this.allergyForm.private = item.private
      this.originalAttachments = item.attachments.map((element) => {
        return { ...element }
      })
    },
  },
}
</script>

<template>
  <v-container @dragover.prevent @drop="onDrop">
    <v-row
      no-gutters
      :class="$style.hrHeader"
      class="primary--text mt-1"
      justify="center"
    >
      <div class="mr-4">
        <v-img
          :src="
            require('@assets/images/safehaven/health-record-icons/allergies.svg')
          "
          height="30"
          width="30"
          contain
        ></v-img>
      </div>
      {{ $t('health-record.allergies.allergy') }}
    </v-row>

    <v-row v-if="dateError" class="ml-1">
      <v-col cols="12" md="12">
        <p class="error--text">{{ dateError }}</p>
      </v-col>
    </v-row>

    <v-row
      :class="$style.hrQuestionFull"
      class="mt-10 offblack--text"
      no-gutters
      justify="center"
    >
      When was the first time you remember this allergy occurring?
    </v-row>

    <v-row class="mt-10" no-gutters justify="center">
      <div
        :class="mobile ? $style.medicalDateDivMobile : $style.medicalDateDiv"
      >
        <MedicalDatePickerDialog
          :key="'allergyDate-1'"
          :dialog.sync="medicalDateStartPickerDialog"
          :title="'health-record.allergies.start-date'"
          :field="allergyForm.start"
          :part="allergyForm.start_date_part"
          filled
          :disabled="show"
          background-color="input"
          :error-messages="
            allergyForm.errors.has('start')
              ? 'Start Date: ' + allergyForm.errors.get('start')
              : ''
          "
          @input="clearErrors('start', 'allergyForm', 'allergyError')"
          @setMedicalDate="setMedicalDate"
          @showMedicalDate="showMedicalDate('start')"
        />
      </div>
    </v-row>

    <v-row
      :class="$style.hrQuestionFull"
      class="mt-7 offblack--text"
      no-gutters
      justify="center"
    >
      What about last occurrence?
    </v-row>
    <v-row class="mt-10" no-gutters justify="center">
      <div
        :class="mobile ? $style.medicalDateDivMobile : $style.medicalDateDiv"
      >
        <MedicalDatePickerDialog
          :key="'allergyDate-2'"
          :dialog.sync="medicalDateEndPickerDialog"
          :title="'health-record.allergies.end-date'"
          :field="allergyForm.last_occurrence"
          :part="allergyForm.last_occurrence_date_part"
          :disabled="show"
          filled
          background-color="input"
          @input="dateError = null"
          @setMedicalDate="setMedicalDate"
          @showMedicalDate="showMedicalDate('last_occurrence')"
        />
      </div>
    </v-row>

    <v-row
      :class="$style.hrQuestionFull"
      class="mt-7 offblack--text"
      no-gutters
      justify="center"
    >
      What type of allergy is this?
    </v-row>

    <v-row class="mt-10" no-gutters justify="center">
      <v-item-group
        v-model="categoriesFromIcons"
        class="mb-10"
        :disabled="show"
      >
        <v-container :class="$style.cardsContainer">
          <v-row>
            <v-col
              v-for="cat in categories"
              :key="cat.value"
              class="d-flex justify-center"
              cols="12"
              md="4"
            >
              <v-item v-slot="{ active, toggle }" :value="cat" :disabled="show">
                <v-sheet
                  class="d-flex align-center clickable"
                  :class="[
                    active ? [$style.hrCard, $style.activeCard] : $style.hrCard,
                    show ? $style.disabledClass : '',
                  ]"
                  width="156"
                  height="160"
                  flat
                  :color="active ? '#EBECFE' : 'background'"
                  :disabled="show"
                  @click="toggle"
                >
                  <v-card-text>
                    <v-container fluid class="fill-height pa-0">
                      <v-row class="mb-4" justify="center" no-gutters>
                        <v-icon v-show="!active" color="placehold" large>
                          {{ allergyIcons[cat.key] }}
                        </v-icon>
                        <v-img
                          v-show="active"
                          :disabled="show"
                          contain
                          max-height="36"
                          max-width="36"
                          :src="require('@assets/images/checkmark.svg')"
                        ></v-img>
                      </v-row>
                      <v-row
                        :class="$style.cardText"
                        justify="center"
                        class="label--text"
                        no-gutters
                      >
                        {{ $t(cat.value) }}</v-row
                      >
                    </v-container>
                  </v-card-text>
                </v-sheet>
              </v-item>
            </v-col>
            <v-col
              v-if="!categoriesFromIcons && allergyForm.errors.has('category')"
              :class="['error--text', $style.errorText]"
              cols="12"
            >
              Category: {{ allergyForm.errors.get('category') }}
            </v-col>
          </v-row>
        </v-container>
      </v-item-group>
    </v-row>

    <v-row
      class="mt-7 offblack--text"
      :class="$style.hrQuestionFull"
      no-gutters
      justify="center"
    >
      What is causing your allergy?
    </v-row>
    <v-row class="mt-10" no-gutters justify="center">
      <div :class="$style.hrComboBoxContainer">
        <ComboBox
          :key="'allergy-combo-1'"
          :disabled="show"
          :combo-class="'allergy-combo-1'"
          :value.sync="allergyForm.allergy"
          :placeholder="allergyTypeExample"
          :error-messages="
            allergyForm.errors.has('allergy')
              ? 'Allergy: ' + allergyForm.errors.get('allergy')
              : ''
          "
          :fetch-url="unauth ? null : '/api/v1/hr/choices/allergy/'"
          :group="allergyForm.category"
          rounded
          filled
          background-color="input"
          :class="$style.hrComboBox"
          @keydown="
            clearFormFromErrors(
              'allergy',
              'allergyForm',
              // eslint-disable-next-line vue/comma-dangle
              'allergyError'
            )
          "
        ></ComboBox>
      </div>
    </v-row>

    <v-row
      :class="$style.hrQuestionFull"
      class="mt-7 offblack--text"
      no-gutters
      justify="center"
    >
      What's the primary symptom?
    </v-row>
    <v-row class="mt-10" no-gutters justify="center">
      <v-autocomplete
        v-model="allergyForm.reaction"
        :disabled="show"
        class="allergySymptom"
        attach=".allergySymptom"
        :items="reactions"
        item-text="value"
        item-value="key"
        placeholder="Symptom"
        rounded
        filled
        background-color="input"
        clearable
        :class="$style.hrAutoComplete"
      ></v-autocomplete>
    </v-row>

    <v-row class="mt-7">
      <div :class="$style.hrQuestionFull" class="offblack--text"
        >How would you rate its severity?
      </div>
    </v-row>
    <v-row class="mt-15">
      <div class="mx-auto" :class="$style.hrInputDiv">
        <ColoredRangeSlider
          v-model="allergyForm.criticality"
          :disabled="show"
          :min="1"
          :max="criticalities.length"
          :step="1"
          class="my-15"
        ></ColoredRangeSlider>
      </div>
    </v-row>

    <v-row class="mt-16">
      <div :class="$style.hrQuestionFull" class="offblack--text"
        >Has this allergy been verified?
      </div>
    </v-row>
    <v-row class="mt-10">
      <div class="mx-auto" :class="$style.hrInputDiv">
        <v-autocomplete
          v-model="allergyForm.verification_status"
          :disabled="show"
          class="allergyVerificationStatus"
          attach=".allergyVerificationStatus"
          :items="verificationStatuses"
          item-text="value"
          item-value="key"
          :error-messages="
            allergyForm.errors.has('verification_status')
              ? 'Verification Status: ' +
                allergyForm.errors.get('verification_status')
              : ''
          "
          :placeholder="$t('health-record.allergies.verification_status')"
          rounded
          filled
          background-color="input"
          clearable
          :class="$style.hrAutoComplete"
          @change="
            clearFormFromErrors(
              'verification_status',
              'allergyForm',
              // eslint-disable-next-line vue/comma-dangle
              'allergyError'
            )
          "
        ></v-autocomplete>
      </div>
    </v-row>

    <v-row class="mt-10">
      <div :class="$style.hrQuestionFull" class="offblack--text"
        >Any additional files or notes you'd like to include?
      </div>
    </v-row>
    <v-row class="mt-10">
      <div class="mx-auto" :class="$style.hrInputDiv">
        <SpeechToText
          :key="'allergy-2'"
          :disabled="show"
          :value.sync="allergyForm.note"
          auto-grow
          rounded
          :class="$style.hrSpeechToText"
          :placeholder="$t('health-record.allergies.note')"
        ></SpeechToText>
      </div>
    </v-row>
    <v-row class="mt-10">
      <div class="mx-auto" :class="$style.hrInputDiv">
        <FileUpload
          :key="'allergy-3'"
          :disabled="show"
          icon
          :height="200"
          :multiple="true"
          :files.sync="attachments"
          :non-draggable="true"
        ></FileUpload>
      </div>
    </v-row>

    <v-row v-if="edit" class="mt-10">
      <div class="mx-auto" :class="$style.hrInputDiv">
        <AttachmentList
          :attachments="originalAttachments"
          :disabled="show"
          @delete-attachment="deleteAttachment"
        ></AttachmentList>
      </div>
    </v-row>

    <ForMyEyesOnly v-if="!show" :value.sync="allergyForm.private" />
    <div v-if="showHRActions">
      <HrActions
        :can-save="canSaveAllergy"
        :is-another="isAnother"
        :is-continue="isContinue"
        :trying-to-save="tryingToSave"
        :trying-to-delete="tryingToDelete"
        :edit="edit"
        @go-back="goBack"
        @try-to-save="tryToSave"
        @try-to-delete="prepareDeletion"
      />
    </div>
    <v-row v-if="allergyError" class="ml-1">
      <v-col cols="12" md="12">
        <p class="error--text">{{ $t('health-record.allergies.error') }}</p>
        <div v-if="allergyForm.errors.has('non_field_errors')">
          <div
            v-for="(error, index) in allergyForm.errors.getNonField(
              // eslint-disable-next-line vue/comma-dangle
              'non_field_errors'
            )"
            :key="'error-non-field' + index"
          >
            <p class="error--text">{{ error }}</p>
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row v-if="message" class="ml-1">
      <p class="success--text">{{ $t('health-record.allergies.message') }}</p>
    </v-row>
    <BaseConfirmDialog
      v-if="confirmDialog"
      :dialog="confirmDialog"
      :message="computedMessage"
      title="Delete Item"
      @cancel="cancel"
      @confirm="deleteItem"
    />
  </v-container>
</template>

<style lang="scss" module>
@import '@design';
.disabledClass {
  pointer-events: none;
}
.hrHeader {
  width: 100%;
  font-family: $system-default-font-family;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
  text-align: center;
  letter-spacing: 1px;
}
.hrQuestion {
  // styleName: Desktop/Display X-Small;
  font-family: $system-default-font-family;
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  text-align: center;
  letter-spacing: 1px;
}
.hrQuestionFull {
  // styleName: Desktop/Display X-Small;
  width: 100%;
  font-family: $system-default-font-family;
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  text-align: center;
  letter-spacing: 1px;
}
.hrRadio {
  // styleName: Mobile / Display Small;
  font-family: $system-default-font-family;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  text-align: left;
  letter-spacing: 1px;
}
.hrComboBoxContainer {
  width: 394px;
}
.hrComboBox {
  border-radius: 15px !important;
}
.hrTextField {
  max-width: 394px !important;
  border-radius: 15px !important;
}
.hrTextFieldCol {
  max-width: 200px !important;
  border-radius: 15px !important;
}
.hrAutoComplete {
  max-width: 394px !important;
  border-radius: 15px !important;
}
.hrAutoCompleteCol {
  max-width: 200px !important;
  border-radius: 15px !important;
}
.hrSelect {
  max-width: 394px !important;
  border-radius: 15px !important;
}
.hrInputDiv {
  width: 394px;
  border-radius: 15px !important;
}
.hrSpeechToText {
  max-width: 394px !important;
  background: $color-theme-light-input;
}
.cardsContainer {
  padding-right: 120px !important;
  padding-left: 120px !important;
}
.hrCard {
  border-radius: 16px !important;
}
.activeCard {
  border: 1px solid $color-theme-light-secondary !important;
}
.hrButton {
  border-radius: 8px !important;
}
.hrButtonOutlined {
  border-color: $color-theme-light-primary !important;
  border-radius: 8px !important;
}
.hrRouterLinkText {
  font-family: $system-default-font-family;
  font-size: 20px;
}
.medicalDateDiv {
  width: 394px;
  margin-right: auto;
  margin-left: auto;
}
.medicalDateDivMobile {
  width: 60%;
  margin-right: auto;
  margin-left: auto;
}

.errorText {
  font-size: 12px;
  color: #ca024f !important;
}
</style>
